/**
 * Client brief (approved mockup 2026-10-26): one page to scan before a call. The facts come
 * straight from TechHub's records (GET /clienthub/clients/:id/brief), so they are always
 * current and need no AI. When AI is on for the company, it adds a two-line summary at the top
 * and pulls the cautions out of the notes, each linked to where it came from.
 * Dollar values only for admins and Accounting (the server leaves them out for everyone else).
 */
import { useEffect, useState } from "react";
import { api, type ChBrief, type ChClient } from "../../lib/api";
import { useStore } from "../../lib/store";
import { fmt, fmtDT } from "../../lib/format";
import { Loading } from "../../components/bits";
import { ACT } from "./Client";

const KIND: Record<string, string> = { workstation: "workstations", laptop: "laptops", server: "servers", virtual: "virtual machines", firewall: "firewalls", router: "routers",
  switch: "switches", access_point: "access points", printer: "printers", nas: "NAS", ups: "UPS", phone: "phones", mobile: "mobiles", camera: "cameras", cloud: "cloud", other: "other" };
const STATUS: Record<string, string> = { onboarding: "Onboarding", active: "Active", inactive: "Inactive", former: "Former client" };
const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const sinceText = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });

export function BriefView({ c, onClose }: { c: ChClient; onClose: () => void }) {
  const { toast, ai } = useStore();
  const [b, setB] = useState<ChBrief | null>(null);
  const [aiOut, setAiOut] = useState<Awaited<ReturnType<typeof api.aiBrief>> | null>(null);
  const [aiErr, setAiErr] = useState<string | null>(null);
  const aiOn = ai.on(c.company, "brief");
  const load = () => {
    setB(null); setAiOut(null); setAiErr(null);
    api.chBrief(c.id).then(setB, (e: Error) => toast(e.message));
    if (aiOn) api.aiBrief(c.id).then(setAiOut, (e: Error) => setAiErr(e.message));
  };
  useEffect(load, [c.id, aiOn]);
  if (!b) return <Loading />;

  const copy = async () => {
    const lines = [
      `${b.client.name} (${b.client.code}): client brief, ${fmtDT(b.as_of)}`,
      aiOut ? aiOut.summary : "",
      `Status: ${STATUS[b.client.status] ?? b.client.status}. Client since ${sinceText(b.client.since)}. Account owner: ${b.client.owner_name ?? "not set"}.`,
      b.coming.length ? `Coming up:\n${b.coming.map((x) => `- ${x.title}: ${x.detail}, ${fmt(x.date)} (${x.days} days)`).join("\n")}` : "",
      b.contacts.length ? `Who to call:\n${b.contacts.map((k) => `- ${k.name}${k.title ? `, ${k.title}` : ""}${k.phone ? `: ${k.phone}` : ""}`).join("\n")}` : "",
      aiOut?.cautions.length ? `Watch out for:\n${aiOut.cautions.map((x) => `- ${x.text}`).join("\n")}` : "",
    ].filter(Boolean);
    try { await navigator.clipboard.writeText(lines.join("\n\n")); toast("Copied."); } catch { toast("Couldn't copy. Select the text instead."); }
  };
  const tone = (d: number) => (d <= 14 ? "br-red" : d <= 60 ? "br-amber" : "br-grey");
  const devices = b.run.devices.reduce((n, x) => n + x.n, 0);
  const deviceText = devices ? `${devices}: ${b.run.devices.slice(0, 4).map((x) => `${x.n} ${x.n === 1 ? KIND[x.kind]?.replace(/s$/, "") ?? x.kind : KIND[x.kind] ?? x.kind}`).join(", ")}` : "None recorded";
  const contacts = [...b.contacts].sort((x, y) => (x.escalation_order ?? 99) - (y.escalation_order ?? 99));

  return (
    <div className="card sect br-wrap">
      <div className="br-top">
        <div><div className="eyebrow">Client brief</div><h2 className="x-h2" style={{ margin: "2px 0 0" }}>{b.client.name}</h2>
          <div className="cap">As of {fmtDT(b.as_of)} · from TechHub records</div></div>
        <div className="row-btns hide-print">
          <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>Print</button>
          <button className="btn btn-ghost btn-sm" onClick={() => void copy()}>Copy</button>
          <button className="btn btn-ghost btn-sm" onClick={load}>Refresh</button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
      {aiOn && (
        <div className="br-sum"><span className="ai-dot">AI</span>
          <p>{aiOut ? aiOut.summary : aiErr ? <span className="cap">{aiErr}</span> : <span className="cap">Writing a short summary…</span>}</p></div>
      )}
      <div className="br-kpis">
        <div><span className="cap">Status</span><b>{b.client.status === "active" && <span className="pill-dot ok" />}{STATUS[b.client.status] ?? b.client.status}</b></div>
        <div><span className="cap">Client since</span><b>{sinceText(b.client.since)}</b></div>
        <div><span className="cap">Account owner</span><b>{b.client.owner_name ?? "Not set"}</b></div>
        <div><span className="cap">Last verified</span><b>{b.client.last_verified_at ? fmt(b.client.last_verified_at) : "Never"}</b></div>
        {b.monthly !== null && <div><span className="cap">Monthly <span className="lockbadge money-tag">Admins &amp; Accounting</span></span><b>{money(b.monthly)}</b></div>}
      </div>
      <div className="br-grid">
        <div className="br-box"><h4>Coming up</h4>
          {b.coming.length ? <ul className="br-list">{b.coming.slice(0, 6).map((x, i) => (
            <li key={i} className="br-due"><span className={`br-days ${tone(x.days)}`}>{x.days < 0 ? "overdue" : x.days === 0 ? "today" : `${x.days} day${x.days === 1 ? "" : "s"}`}</span>
              <div><b>{x.title}</b><div className="cap">{x.detail} · {fmt(x.date)}</div></div></li>))}</ul>
            : <div className="cap">Nothing in the next 120 days.</div>}
        </div>
        <div className="br-box"><h4>Who to call</h4>
          {contacts.length ? <table className="br-t"><tbody>{contacts.slice(0, 4).map((k) => (
            <tr key={k.id}><td style={{ width: 30 }}>{k.escalation_order ? <span className="br-esc">{k.escalation_order}</span> : null}</td>
              <td><b>{k.name}</b><div className="cap">{[k.title, k.is_primary ? "primary contact" : ""].filter(Boolean).join(" · ")}{k.notes ? ` · ${k.notes.slice(0, 80)}` : ""}</div></td>
              <td className="br-ph">{k.phone ? <a href={`tel:${k.phone.replace(/[^\d+]/g, "")}`} style={{ color: "inherit", textDecoration: "none" }}>{k.phone}</a> : k.email}</td></tr>))}</tbody></table>
            : <div className="cap">No contacts recorded.</div>}
        </div>
        <div className="br-box"><h4>What we run for them</h4>
          {b.client.services.length > 0 && <div className="br-chips">{b.client.services.map((s) => <span key={s}>{s}</span>)}</div>}
          <dl className="br-dl">
            <dt>Sites</dt><dd>{b.run.sites.join(", ") || "None recorded"}</dd>
            <dt>Devices</dt><dd>{deviceText}</dd>
            <dt>Internet</dt><dd>{b.run.internet.join(", ") || "Not recorded"}</dd>
            {b.run.firewalls.length > 0 && <><dt>Firewall</dt><dd>{b.run.firewalls.join(", ")}</dd></>}
          </dl>
        </div>
        <div className="br-box br-warn"><h4>Watch out for {aiOn && <span className="br-from"><span className="ai-dot sm">AI</span> from the notes</span>}</h4>
          {!aiOn ? <div className="cap">Turn on AI in Settings &gt; AI to pull cautions out of the notes. Until then, check the Notes tab.</div>
            : !aiOut ? <div className="cap">{aiErr ? "Not available right now." : "Reading the notes…"}</div>
            : aiOut.cautions.length ? <ul className="br-bul">{aiOut.cautions.map((x, i) => <li key={i}><b>{x.text}</b> <span className="br-src">{x.source.label}</span></li>)}</ul>
            : <div className="cap">Nothing in the notes to watch out for.</div>}
        </div>
      </div>
      <div className="br-box"><h4>Recently</h4>
        {b.recent.length ? <ul className="br-bul br-recent">{b.recent.map((r, i) => (
          <li key={i}><span className="cap">{fmt(r.at).replace(/, \d{4}$/, "")}</span><span>{r.actor_name ?? "Someone"} {ACT[r.action] ?? r.action.replace(/^clienthub\./, "").replace(/\./g, " ")}</span></li>))}</ul>
          : <div className="cap">Nothing yet.</div>}
      </div>
    </div>
  );
}
